import React, {forwardRef, ReactElement, useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import {Clear, Search, ArrowDownward, Edit, Delete} from '@material-ui/icons';
import {
  Checkbox,
  makeStyles,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Link
} from '@material-ui/core';
import MaterialTable, {Icons} from 'material-table';

import {
  getAllUsers,
  updateUserType,
  updateUserCanImpersonate,
  getModules,
  createUser,
  updateUser,
  deleteUser
} from '../../../../shared/src/services/UserService';
import DateTimeUtils from '../../../../shared/src/utils/DateTimeUtils';
import {_snackbarAcknowledgement} from '../../../../shared/src/store/actions/AuthActions';

import './Users.scss';

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiToolbar-regular': {
      minHeight: 0
    }
  },
  button: {
    background: '#F7931E',
    borderRadius: 8,
    width: '6.1vw',
    height: '55%',
    textAlign: 'center',
    justifyContent: 'ceter',
    alignItems: 'center',
    textTransform: 'none',
    float: 'right',
    marginRight: '2%'
  },
  saveButton: {
    borderRadius: 8,
    width: '6.1vw',
    textTransform: 'none',
    float: 'right'
  },
  dialog: {
    '& .MuiDialog-paperScrollPaper': {
      borderRadius: 8
    }
  },
  nameRow: {
    display: 'flex',
    gap: 12
  },
  nameField: {
    flex: 1
  },
  modulesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))',
    columnGap: 24
  }
}));

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Users = (props: any): ReactElement => {
  const {t} = useTranslation();
  const classes = useStyles();
  const tableIcons: Icons = {
    ResetSearch: forwardRef((prop, ref) => <Clear {...prop} ref={ref} />),
    Search: forwardRef((prop, ref) => <Search {...prop} ref={ref} />),
    SortArrow: forwardRef((prop, ref) => <ArrowDownward {...prop} ref={ref} />)
  };
  const stylesObject: any = {
    width: '95.7%',
    fontFamily: 'hindGunturMedium',
    fontSize: '0.9vw'
  };

  const optionsObject: any = {
    search: true,
    paging: false,
    header: true,
    showTitle: false,
    sorting: true,
    searchAutoFocus: true,
    searchFieldStyle: {
      marginTop: '-5vw',
      width: '22vw',
      height: '3.2vw',
      border: '1px solid #D0D0D0',
      borderRadius: 3,
      backgroundColor: '#FFFFFF',
      paddingTop: 4,
      paddingLeft: '5%',
      marginRight: '7vw'
    },
    headerStyle: {
      color: '#777B7E',
      fontFamily: 'workSansSemiBold',
      height: '3vw',
      fontSize: '0.75vw',
      textAlign: 'left'
    }
  };
  const [usersData, setUsersData]: any = useState([]);
  const [availableModules, setAvailableModules] = useState<any[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    modules: [] as number[]
  });

  const processUsersData = (response: any): void => {
    const values: any = [];
    response.forEach((element: any) => {
      values.push({
        name: element.first_name + ' ' + (element.last_name || ''),
        firstName: element.first_name || '',
        lastName: element.last_name || '',
        email: element.email,
        created: DateTimeUtils.formatDateDMYWithSlashesAndZeros(element.created_at),
        lastLogin: DateTimeUtils.formatDateDMYWithSlashesAndZeros(
          element.last_login_date,
          true
        ),
        lastLogout: DateTimeUtils.formatDateDMYWithSlashesAndZeros(
          element.last_logout,
          true
        ),
        userType: element.user_type,
        id: element.id,
        canImpersonate: element.can_impersonate,
        modules: (element.userModules || [])
          .map((item: any) => item?.module?.id)
          .filter((id: any) => !!id)
      });
    });
    setUsersData(values);
  };

  const fetchUsersInformation = async (): Promise<void> => {
    try {
      const response = await getAllUsers();
      processUsersData(response?.rows || []);
    } catch (error) {
      props._snackbarAcknowledgement(true, t('COMMON.GETERROR'), t('COMMON.ERRORTEXT'));
    }
  };

  const fetchModules = async (): Promise<void> => {
    try {
      const response = await getModules();
      setAvailableModules(response || []);
    } catch (error) {
      setAvailableModules([]);
    }
  };

  useEffect(() => {
    fetchUsersInformation();
    fetchModules();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetDialogState = (): void => {
    setFormState({
      firstName: '',
      lastName: '',
      email: '',
      modules: []
    });
    setSelectedUser(null);
  };

  const openCreateDialog = (): void => {
    resetDialogState();
    setIsDialogOpen(true);
  };

  const openEditDialog = (rowData: any): void => {
    setSelectedUser(rowData);
    setFormState({
      firstName: rowData.firstName,
      lastName: rowData.lastName,
      email: rowData.email,
      modules: rowData.modules || []
    });
    setIsDialogOpen(true);
  };

  const closeDialog = (): void => {
    setIsDialogOpen(false);
    resetDialogState();
  };

  const openDeleteDialog = (rowData: any): void => {
    setSelectedUser(rowData);
    setIsDeleteDialogOpen(true);
  };

  const closeDeleteDialog = (): void => {
    setIsDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const toggleModule = (moduleId: number): void => {
    setFormState(prev => {
      const exists = prev.modules.includes(moduleId);
      return {
        ...prev,
        modules: exists
          ? prev.modules.filter((id: number) => id !== moduleId)
          : [...prev.modules, moduleId]
      };
    });
  };

  const submitUser = async (): Promise<void> => {
    if (!formState.firstName || !formState.lastName || !emailRegex.test(formState.email)) {
      props._snackbarAcknowledgement(true, 'Please enter valid user details', t('COMMON.ERRORTEXT'));
      return;
    }
    if (!formState.modules || formState.modules.length === 0) {
      props._snackbarAcknowledgement(
        true,
        'Please select at least one module',
        t('COMMON.ERRORTEXT')
      );
      return;
    }
    try {
      const payload = {
        firstName: formState.firstName,
        lastName: formState.lastName,
        email: formState.email,
        modules: formState.modules
      };
      if (selectedUser?.id) {
        await updateUser(selectedUser.id, payload);
      } else {
        await createUser(payload);
      }
      closeDialog();
      fetchUsersInformation();
    } catch (error: any) {
      props._snackbarAcknowledgement(true, error.response.data.message || t('COMMON.UPDATEERROR'), t('COMMON.ERRORTEXT'));
    }
  };

  const confirmDelete = async (): Promise<void> => {
    try {
      await deleteUser(selectedUser.id);
      closeDeleteDialog();
      fetchUsersInformation();
    } catch (error: any) {
      props._snackbarAcknowledgement(true, error.response.data.message || t('COMMON.UPDATEERROR'), t('COMMON.ERRORTEXT'));
    }
  };

  const updateType = async (event: any, rowData: any): Promise<void> => {
    const checked = event && event.target && event.target.checked;
    try {
      const response = await updateUserType(rowData.id, rowData);
      if (response) {
        const list = [...usersData];
        list.forEach((element: any) => {
          if (element.id === rowData.id) {
            if (checked) {
              element.userType = t('COMMON.ADMIN');
            } else {
              element.userType = t('COMMON.USER');
            }
            setUsersData(list);
          }
        });
      }
    } catch (error) {
      props._snackbarAcknowledgement(
        true,
        t('COMMON.UPDATEERROR'),
        t('COMMON.ERRORTEXT')
      );
    }
  };

  const updateCanImpersonate = async (
    event: any,
    rowData: any
  ): Promise<void> => {
    try {
      const checked = event && event.target && event.target.checked;
      rowData.canImpersonate = checked;
      const response = await updateUserCanImpersonate(rowData.id, rowData);
      if (response) {
        const list = [...usersData];
        list.forEach((element: any) => {
          if (element.id === rowData.id) {
            element.canImpersonate = checked;
            setUsersData(list);
          }
        });
      }
    } catch (error) {
      props._snackbarAcknowledgement(
        true,
        t('COMMON.UPDATEERROR'),
        t('COMMON.ERRORTEXT')
      );
    }
  };

  const getUserType = (rowData: any): boolean => {
    if (
      rowData.userType === t('COMMON.ADMIN') ||
      rowData.userType === t('COMMON.SUPERADMIN')
    )
      return true;
    else return false;
  };

  const checkSuperUser = (rowData: any): boolean => {
    if (rowData.userType === t('COMMON.SUPERADMIN')) return true;
    else return false;
  };

  const columnsList: any = [
    {title: 'NAME', field: 'name', cellStyle: {textAlign: 'left'}},
    {title: 'EMAIL', field: 'email', cellStyle: {textAlign: 'left'}},
    {
      title: 'IS ADMIN',
      field: 'userType',
      cellStyle: {textAlign: 'left', width: '15%'},
      render: (rowData: any) => (
        <Checkbox
          key={rowData.id}
          checked={getUserType(rowData)}
          onChange={(event: any) => updateType(event, rowData)}
          name={rowData.name}
          color="primary"
          disabled={checkSuperUser(rowData)}
        />
      )
    },
    {
      title: 'CAN IMPERSONATE',
      field: 'userType',
      cellStyle: {textAlign: 'left', width: '15%'},
      render: (rowData: any) => (
        <Checkbox
          key={rowData.id}
          checked={rowData.canImpersonate}
          onChange={(event: any) => updateCanImpersonate(event, rowData)}
          name={rowData.name}
          color="primary"
          disabled={checkSuperUser(rowData)}
        />
      )
    },
    {
      title: 'CREATED',
      field: 'created',
      cellStyle: {textAlign: 'right', width: '8%'}
    },
    {
      title: 'LAST LOGIN',
      field: 'lastLogin',
      cellStyle: {textAlign: 'right', width: '13%'}
    },
    {
      title: 'LAST LOGOUT',
      field: 'lastLogout',
      cellStyle: {textAlign: 'right', width: '13%'}
    },
    {
      title: 'ACTIONS',
      field: 'actions',
      sorting: false,
      render: (rowData: any) => {
        const isSuperAdmin = checkSuperUser(rowData);
        return (
          <div style={{display: 'flex'}}>
            <Edit
              style={{
                cursor: isSuperAdmin ? 'not-allowed' : 'pointer',
                marginRight: 12,
                opacity: isSuperAdmin ? 0.4 : 1
              }}
              onClick={() => {
                if (!isSuperAdmin) {
                  openEditDialog(rowData);
                }
              }}
            />
            <Delete
              style={{
                cursor: isSuperAdmin ? 'not-allowed' : 'pointer',
                opacity: isSuperAdmin ? 0.4 : 1
              }}
              onClick={() => {
                if (!isSuperAdmin) {
                  openDeleteDialog(rowData);
                }
              }}
            />
          </div>
        );
      }
    }
  ];

  return (
    <div className="users-wrapper">
      <div className="users-view">
        <div className="users-container">
          <div className="users-text">{t('USERS.AUTHENTICATEDUSERS')}</div>
          </div>
          <div className="button-container">
            <Button
              variant="contained"
              className={classes.button}
              onClick={openCreateDialog}>
              <span className="button-text">{t('COMMON.ADD')}</span>
            </Button>
          </div>
        </div>
        <div className={classes.root} style={{ marginLeft: '6%' }}>
          <MaterialTable
            icons={tableIcons}
            style={stylesObject}
            columns={columnsList}
            data={usersData}
            options={optionsObject}
          />
        </div>
        <Dialog open={isDialogOpen} onClose={closeDialog} maxWidth={'md'} className={classes.dialog}>
          <DialogContent>
            <div className="form-container">
              <div className="title-container">
                <div className="title-view">
                  <span className="title-text">{selectedUser ? 'Edit User' : 'Add User'}</span>
                </div>
                <div className="close-view">
                  <Link component="button" variant="body2" onClick={closeDialog}>
                    <span className="close-view-text">{t('PROCEDUREFLOWHEADER.CLOSE')}</span>
                  </Link>
                </div>
              </div>
              <div className={classes.nameRow}>
                <TextField
                  className={[classes.nameField, 'text-field-input'].join(' ')}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  label="First Name"
                  value={formState.firstName}
                  onChange={e => setFormState({...formState, firstName: e.target.value})}
                />
                <TextField
                  className={[classes.nameField, 'text-field-input'].join(' ')}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  label="Last Name"
                  value={formState.lastName}
                  onChange={e => setFormState({...formState, lastName: e.target.value})}
                />
              </div>
              <TextField
                className="text-field-input"
                margin="dense"
                variant="outlined"
                fullWidth
                label="Email"
                value={formState.email}
                onChange={e => setFormState({...formState, email: e.target.value})}
                error={!!formState.email && !emailRegex.test(formState.email)}
                helperText={
                  !!formState.email && !emailRegex.test(formState.email)
                    ? 'Enter a valid email'
                    : ''
                }
              />
              <div style={{marginTop: 16}}>
                <div style={{fontWeight: 600, marginBottom: 6}}>Modules</div>
                <div className={classes.modulesGrid}>
                  {availableModules.map((module: any) => (
                    <div key={module.id}>
                      <Checkbox
                        checked={formState.modules.includes(module.id)}
                        onChange={() => toggleModule(module.id)}
                        color="primary"
                      />
                      {module.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions className="actions-container">
            <Button
              variant="contained"
              className={[classes.saveButton, 'button'].join(' ')}
              onClick={submitUser}>
              <span className="button-text">{selectedUser ? t('COMMON.UPDATE') : t('COMMON.ADD')}</span>
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={isDeleteDialogOpen}
          onClose={closeDeleteDialog}
          maxWidth={'md'}
          className={classes.dialog}>
          <DialogContent>
            <div className="form-container">
              <div className="title-container">
                <div className="title-view">
                  <span className="title-text">{t('COMMON.DELETE')}</span>
                </div>
                <div className="close-view">
                  <Link component="button" variant="body2" onClick={closeDeleteDialog}>
                    <span className="close-view-text">{t('PROCEDUREFLOWHEADER.CLOSE')}</span>
                  </Link>
                </div>
              </div>
              <div style={{paddingTop: 8}}>
                {`Are you sure you want to delete ${selectedUser?.name || 'this user'}?`}
              </div>
            </div>
          </DialogContent>
          <DialogActions className="actions-container">
            <Button
              variant="contained"
              className={[classes.saveButton, 'button'].join(' ')}
              onClick={confirmDelete}>
              <span className="button-text">{t('COMMON.DELETE')}</span>
            </Button>
          </DialogActions>
        </Dialog>
    </div>
  );
};

function mapStateToProps(state: any) {
  return {};
}

export default connect(mapStateToProps, {_snackbarAcknowledgement})(Users);
